import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { format } from 'date-fns';
import { Link } from 'react-router-dom';
import PageWrapper from 'components/PageWrapper';
import useToaster from 'utils/hooks/useToaster';
import KjedeNedleggelseApi from 'services/KjederegisteretAdminApi/KjedeNedleggelseApi';
import {
  KjedeNedleggelseKandidatDto,
  KjedeNedleggelseResultat,
} from 'services/KjederegisteretAdminApi/KjedeNedleggelseApi/types';

const today = () => format(new Date(), 'yyyy-MM-dd');

const formatDate = (date: Date | null | undefined) =>
  date ? format(new Date(date), 'dd.MM.yyyy') : '–';

type SortKey = 'kjedenr' | 'kjedenavn' | 'overliggende' | 'hovedkontor' | 'antall' | 'sisteMedlem';

const sortValue = (k: KjedeNedleggelseKandidatDto, key: SortKey): string | number => {
  switch (key) {
    case 'kjedenr':
      return k.kjedenr;
    case 'kjedenavn':
      return k.kjedenavn.toLowerCase();
    case 'overliggende':
      return k.aapneKnytninger
        .map((p) => p.kjedenavnOver)
        .join(', ')
        .toLowerCase();
    case 'hovedkontor':
      return (k.hovedkontorNavn ?? '').toLowerCase();
    case 'antall':
      return k.antallAktiveMedlemmer;
    case 'sisteMedlem':
      return k.sisteMedlemskapTilOgMedDato ? new Date(k.sisteMedlemskapTilOgMedDato).getTime() : 0;
  }
};

const errorMessage = (e: any) => e?.response?.data?.detail ?? e?.message ?? 'Ukjent feil';

const KjederTilNedleggelse = () => {
  const toaster = useToaster();
  const [kandidater, setKandidater] = useState<KjedeNedleggelseKandidatDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [medlemFilter, setMedlemFilter] = useState('alle');
  const [sort, setSort] = useState<{ key: SortKey; asc: boolean }>({ key: 'kjedenavn', asc: true });
  const [valgte, setValgte] = useState<Set<number>>(new Set());
  const [bekreft, setBekreft] = useState<KjedeNedleggelseKandidatDto[] | null>(null);
  const [dato, setDato] = useState(today());
  const [isSaving, setIsSaving] = useState(false);
  const [feil, setFeil] = useState<KjedeNedleggelseResultat[]>([]);

  const hent = useCallback(() => {
    setIsLoading(true);
    setError(null);
    KjedeNedleggelseApi.getKandidater()
      .then((data) => {
        setKandidater(data);
        setValgte(
          (prev) => new Set(Array.from(prev).filter((id) => data.some((k) => k.kjedeID === id))),
        );
      })
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setIsLoading(false));
  }, []);

  useEffect(hent, [hent]);

  const synlige = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return kandidater
      .filter((k) => medlemFilter === 'alle' || k.antallAktiveMedlemmer === Number(medlemFilter))
      .filter(
        (k) =>
          !q ||
          k.kjedenavn.toLowerCase().includes(q) ||
          k.kjedenr.toString().includes(q) ||
          (k.hovedkontorNavn ?? '').toLowerCase().includes(q),
      )
      .sort((a, b) => {
        const av = sortValue(a, sort.key);
        const bv = sortValue(b, sort.key);
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return sort.asc ? cmp : -cmp;
      });
  }, [kandidater, filter, medlemFilter, sort]);

  const toggleSort = (key: SortKey) =>
    setSort((prev) => ({ key, asc: prev.key === key ? !prev.asc : true }));

  const sortHeader = (key: SortKey, label: string) => (
    <th
      role="button"
      style={{ cursor: 'pointer', userSelect: 'none' }}
      aria-sort={sort.key === key ? (sort.asc ? 'ascending' : 'descending') : 'none'}
      onClick={() => toggleSort(key)}
    >
      {label}
      {sort.key === key ? (sort.asc ? ' ▲' : ' ▼') : ''}
    </th>
  );

  const kanLeggesNed = (k: KjedeNedleggelseKandidatDto) => k.antallAktiveMedlemmer === 0;
  const nedleggbare = synlige.filter(kanLeggesNed);
  const alleSynligeValgt =
    nedleggbare.length > 0 && nedleggbare.every((k) => valgte.has(k.kjedeID));

  const toggle = (id: number) =>
    setValgte((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAlle = () =>
    setValgte((prev) => {
      const next = new Set(prev);
      nedleggbare.forEach((k) => (alleSynligeValgt ? next.delete(k.kjedeID) : next.add(k.kjedeID)));
      return next;
    });

  const utfør = () => {
    if (!bekreft) return;
    setIsSaving(true);
    KjedeNedleggelseApi.leggNed(
      bekreft.map((k) => k.kjedeID),
      dato,
    )
      .then((resultater) => {
        const ok = resultater.filter((r) => r.lagtNed).length;
        const mislykkede = resultater.filter((r) => !r.lagtNed);
        if (ok > 0) toaster.success('Kjeder lagt ned', `${ok} kjede(r) ble lagt ned.`);
        setFeil(mislykkede);
        setBekreft(null);
        hent();
      })
      .catch((e) => toaster.error('Kunne ikke legge ned kjeder', errorMessage(e)))
      .finally(() => setIsSaving(false));
  };

  const navnForId = (id: number) => kandidater.find((k) => k.kjedeID === id)?.kjedenavn ?? id;

  return (
    <PageWrapper header="Kjeder til nedleggelse" isFluid>
      <p className="text-muted">
        Åpne kjeder med tre eller færre aktive eller kommende medlemmer. Hovedkontorets eget
        medlemskap og nedlagte enheter telles ikke. Kjeder med medlemmer kan ikke legges ned før
        medlemmene er flyttet eller avsluttet. Sjekk hovedkontoret før du legger ned – ingenting
        legges ned automatisk.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}
      {feil.length > 0 && (
        <Alert variant="warning" dismissible onClose={() => setFeil([])}>
          <strong>Disse kjedene ble ikke lagt ned:</strong>
          <ul className="mb-0">
            {feil.map((f) => (
              <li key={f.kjedeID}>
                {navnForId(f.kjedeID)}: {f.feilmelding}
              </li>
            ))}
          </ul>
        </Alert>
      )}

      <div className="d-flex gap-2 align-items-center mb-3">
        <Form.Control
          style={{ maxWidth: '20rem' }}
          placeholder="Filtrer på kjedenavn, kjedenr eller hovedkontor"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <Form.Select
          style={{ maxWidth: '12rem' }}
          aria-label="Filtrer på antall aktive medlemmer"
          value={medlemFilter}
          onChange={(e) => setMedlemFilter(e.target.value)}
        >
          <option value="alle">Alle antall</option>
          <option value="0">0 medlemmer</option>
          <option value="1">1 medlem</option>
          <option value="2">2 medlemmer</option>
          <option value="3">3 medlemmer</option>
        </Form.Select>
        <Button variant="outline-secondary" onClick={hent} disabled={isLoading}>
          Oppdater
        </Button>
        <Button
          variant="danger"
          disabled={valgte.size === 0}
          onClick={() => setBekreft(kandidater.filter((k) => valgte.has(k.kjedeID)))}
        >
          Legg ned valgte ({valgte.size})
        </Button>
        <span className="text-muted ms-auto">{synlige.length} kandidater</span>
      </div>

      {isLoading ? (
        <div className="text-center py-5">
          <Spinner animation="border" />
        </div>
      ) : (
        <Table striped bordered hover size="sm" responsive>
          <thead>
            <tr>
              <th style={{ width: '2rem' }}>
                <Form.Check
                  aria-label="Velg alle"
                  checked={alleSynligeValgt}
                  onChange={toggleAlle}
                />
              </th>
              {sortHeader('kjedenr', 'Kjedenr')}
              {sortHeader('kjedenavn', 'Kjede')}
              {sortHeader('overliggende', 'Overliggende kjede')}
              {sortHeader('hovedkontor', 'Hovedkontor')}
              {sortHeader('antall', 'Aktive medlemmer')}
              {sortHeader('sisteMedlem', 'Siste medlem avsluttet')}
              <th />
            </tr>
          </thead>
          <tbody>
            {synlige.map((k) => (
              <tr key={k.kjedeID}>
                <td>
                  <Form.Check
                    aria-label={`Velg ${k.kjedenavn}`}
                    checked={valgte.has(k.kjedeID)}
                    disabled={!kanLeggesNed(k)}
                    onChange={() => toggle(k.kjedeID)}
                  />
                </td>
                <td>{k.kjedenr}</td>
                <td>{k.kjedenavn}</td>
                <td>{k.aapneKnytninger.map((p) => p.kjedenavnOver).join(', ') || '–'}</td>
                <td>
                  <Link
                    to={`/enheter/${k.hovedkontorEnhetId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {k.hovedkontorNavn || k.hovedkontorEnhetId}
                  </Link>
                </td>
                <td>{k.antallAktiveMedlemmer}</td>
                <td>{formatDate(k.sisteMedlemskapTilOgMedDato)}</td>
                <td className="text-end">
                  <Button
                    size="sm"
                    variant="outline-danger"
                    disabled={!kanLeggesNed(k)}
                    title={kanLeggesNed(k) ? undefined : 'Kjeden har aktive medlemmer'}
                    onClick={() => setBekreft([k])}
                  >
                    Legg ned
                  </Button>
                </td>
              </tr>
            ))}
            {synlige.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-3">
                  Ingen kjeder med få eller ingen aktive medlemmer.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}

      <Modal show={bekreft !== null} onHide={() => !isSaving && setBekreft(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Legg ned {bekreft?.length === 1 ? 'kjede' : 'kjeder'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Dette avslutter hovedkontorets medlemskap og alle åpne kjedekoblinger for:</p>
          <ul>
            {bekreft?.map((k) => (
              <li key={k.kjedeID}>
                {k.kjedenavn} ({k.kjedenr})
              </li>
            ))}
          </ul>
          <Form.Group>
            <Form.Label>Nedleggelsesdato</Form.Label>
            <Form.Control type="date" value={dato} onChange={(e) => setDato(e.target.value)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setBekreft(null)} disabled={isSaving}>
            Avbryt
          </Button>
          <Button variant="danger" onClick={utfør} disabled={isSaving || !dato}>
            {isSaving ? <Spinner animation="border" size="sm" /> : 'Legg ned'}
          </Button>
        </Modal.Footer>
      </Modal>
    </PageWrapper>
  );
};

export default KjederTilNedleggelse;
